import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';
import type {
    ClusterDaySelection,
    ClusterKind,
    RateSnapshotData,
} from './types';
import { previewSnapshot, createSnapshot } from './api';
import { getRoomChain } from '@digitalopsdev/serviceops-reservations-components/dist/EditReservation/model.Rooms';
import { getProgramChain } from '@digitalopsdev/serviceops-reservations-components/dist/EditReservation/model.Programs';
import type { ReservationState, RoomBookingState } from '@digitalopsdev/serviceops-reservations-components/dist/EditReservation/types';
import type { ProgramBookingState } from '@digitalopsdev/serviceops-reservations-components/dist/EditReservation/model.Programs';
import { CreateClusterListPanel } from './CreateClusterListPanel';
import { CreateClusterEditorPanel } from './CreateClusterEditorPanel';
import type { RoomIsland, RoomIslandEntry, ProgramIsland, ProgramIslandEntry } from './ClusterAssignmentPanel';
import { getClusterCurrentDays } from './clusterUtils';

interface Props {
    reservationState: ReservationState;
    onClose: () => void;
    onCreated?: () => void;
}

function formatDate(d: Date): string {
    return d.toISOString().split('T')[0];
}

export function CreateRateSnapshotPanel({ reservationState, onClose, onCreated }: Props) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [editingClusterId, setEditingClusterId] = useState<string | null>(null);
    const [clusterSelections, setClusterSelections] = useState<Map<string, Set<string>>>(new Map());
    const [targetCurrent, setTargetCurrent] = useState<RateSnapshotData | null>(null);
    const [saving, setSaving] = useState(false);

    const reservationId = reservationState.ReservationId;
    const roomTypes = reservationState.RoomTypes ?? {};
    const programTypes = reservationState.ProgramTypes ?? {};

    const roomTypeNames: Record<string, string> = useMemo(() => {
        const out: Record<string, string> = {};
        for (const [key, rt] of Object.entries(roomTypes)) {
            if (rt?.Name) out[key] = rt.Name;
        }
        return out;
    }, [roomTypes]);

    const programTypeNames: Record<string, string> = useMemo(() => {
        const out: Record<string, string> = {};
        for (const [key, pt] of Object.entries(programTypes)) {
            if (pt?.Name) out[key] = pt.Name;
        }
        return out;
    }, [programTypes]);

    const roomIslands = useMemo<RoomIsland[]>(() => {
        const allRooms = reservationState.RoomBookings.toArray();
        const visited = new Set<string>();
        const result: RoomIsland[] = [];

        for (const room of allRooms) {
            if (visited.has(room.ReservationRoomId)) continue;
            const chain = getRoomChain(room.ReservationRoomId, allRooms);
            chain.forEach(r => visited.add(r.ReservationRoomId));

            const entries: RoomIslandEntry[] = chain.map(r => ({
                reservationRoomId: r.ReservationRoomId,
                roomType: r.RoomType.value,
                displayName: roomTypes[r.RoomType.value]?.Name ?? r.RoomType.value ?? 'Unknown',
                checkIn: formatDate(new Date(r.CheckIn.value)),
                checkOut: formatDate(new Date(r.CheckOut.value)),
            }));

            const startDate = entries[0].checkIn;
            const endDate = entries[entries.length - 1].checkOut;

            result.push({
                id: chain[0].ReservationRoomId,
                chain: entries,
                dateRange: `${startDate} — ${endDate}`,
                displayName: entries[0].displayName,
                startDate,
                endDate,
            });
        }
        return result;
    }, [reservationState.RoomBookings]);

    const programIslands = useMemo<ProgramIsland[]>(() => {
        const allPrograms = reservationState.ProgramBookings.toArray();
        const visited = new Set<string>();
        const result: ProgramIsland[] = [];

        for (const program of allPrograms) {
            if (visited.has(program.ReservationProgramId)) continue;
            const chain = getProgramChain(program.ReservationProgramId, allPrograms);
            chain.forEach(p => visited.add(p.ReservationProgramId));

            const entries: ProgramIslandEntry[] = chain.map(p => ({
                reservationProgramId: p.ReservationProgramId,
                programType: p.ProgramType.value,
                displayName: programTypes[p.ProgramType.value]?.Name ?? p.ProgramType.value,
                start: formatDate(new Date(p.Start.value)),
                end: formatDate(new Date(p.End.value)),
            }));

            const startDate = entries[0].start;
            const endDate = entries[entries.length - 1].end;

            result.push({
                id: chain[0].ReservationProgramId,
                chain: entries,
                dateRange: `${startDate} — ${endDate}`,
                displayName: entries[0].displayName,
                startDate,
                endDate,
            });
        }
        return result;
    }, [reservationState.ProgramBookings]);

    // Build per-cluster sorted, deduped list of all booking dates from the reservation state.
    const clusterAllDates = useMemo<Map<string, string[]>>(() => {
        const m = new Map<string, string[]>();
        const allRooms = reservationState.RoomBookings.toArray();
        const allPrograms = reservationState.ProgramBookings.toArray();

        for (const island of roomIslands) {
            const dates = new Set<string>();
            for (const entry of island.chain) {
                const room = allRooms.find((r: RoomBookingState) => r.ReservationRoomId === entry.reservationRoomId);
                if (!room) continue;
                for (const d of room.Dates) {
                    dates.add(formatDate(new Date(d.Date)));
                }
            }
            m.set(island.id, [...dates].sort());
        }

        for (const island of programIslands) {
            const dates = new Set<string>();
            for (const entry of island.chain) {
                const program = allPrograms.find((p: ProgramBookingState) => p.ReservationProgramId === entry.reservationProgramId);
                if (!program) continue;
                for (const d of program.Dates) {
                    dates.add(formatDate(new Date(d.Date)));
                }
            }
            m.set(island.id, [...dates].sort());
        }

        return m;
    }, [roomIslands, programIslands, reservationState.RoomBookings, reservationState.ProgramBookings]);

    // Initialize selections to all dates per cluster on first render (and whenever cluster set changes).
    useEffect(() => {
        setClusterSelections(prev => {
            const next = new Map<string, Set<string>>();
            for (const [id, dates] of clusterAllDates) {
                next.set(id, prev.get(id) ?? new Set(dates));
            }
            return next;
        });
    }, [clusterAllDates]);

    // Fetch current rates for the entire reservation once on mount so we can show
    // pricing on the Create tab the same way the Apply tab does.
    useEffect(() => {
        if (!reservationId) return;
        (async () => {
            try {
                const data = await previewSnapshot({
                    reservationId,
                    name: '(target current rates)',
                    clusters: [],
                    roomTypeNames,
                    programTypeNames,
                });
                setTargetCurrent(data);
            } catch {
                setTargetCurrent(null);
            }
        })();
    }, [reservationId, roomTypeNames, programTypeNames]);

    // Build cluster selections payload for save (skip clusters with no selected dates).
    const clustersForRequest = useMemo<ClusterDaySelection[]>(() => {
        const out: ClusterDaySelection[] = [];
        for (const island of roomIslands) {
            const selected = clusterSelections.get(island.id);
            if (!selected || selected.size === 0) continue;
            out.push({
                clusterKind: 'ROOM',
                memberIds: island.chain.map(c => c.reservationRoomId),
                dates: [...selected].sort(),
            });
        }
        for (const island of programIslands) {
            const selected = clusterSelections.get(island.id);
            if (!selected || selected.size === 0) continue;
            out.push({
                clusterKind: 'PROGRAM',
                memberIds: island.chain.map(c => c.reservationProgramId),
                dates: [...selected].sort(),
            });
        }
        return out;
    }, [roomIslands, programIslands, clusterSelections]);

    function setSelectedDatesForEditingCluster(next: Set<string>) {
        if (!editingClusterId) return;
        setClusterSelections(prev => {
            const m = new Map(prev);
            m.set(editingClusterId, next);
            return m;
        });
    }

    async function handleSave() {
        if (!reservationId || !name.trim() || clustersForRequest.length === 0) return;
        setSaving(true);
        try {
            await createSnapshot({
                reservationId,
                name: name.trim(),
                description: description.trim() || undefined,
                clusters: clustersForRequest,
                roomTypeNames,
                programTypeNames,
            });
            onCreated?.();
            onClose();
        } finally {
            setSaving(false);
        }
    }

    function formatCurrency(amount: number) {
        return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    const editingClusterContext = useMemo(() => {
        if (!editingClusterId) return null;
        const roomIsland = roomIslands.find(i => i.id === editingClusterId);
        if (roomIsland) {
            return {
                id: roomIsland.id,
                kind: 'ROOM' as ClusterKind,
                displayName: roomIsland.displayName,
                startDate: roomIsland.startDate,
                endDate: roomIsland.endDate,
                memberIds: roomIsland.chain.map(c => c.reservationRoomId),
            };
        }
        const programIsland = programIslands.find(i => i.id === editingClusterId);
        if (programIsland) {
            return {
                id: programIsland.id,
                kind: 'PROGRAM' as ClusterKind,
                displayName: programIsland.displayName,
                startDate: programIsland.startDate,
                endDate: programIsland.endDate,
                memberIds: programIsland.chain.map(c => c.reservationProgramId),
            };
        }
        return null;
    }, [editingClusterId, roomIslands, programIslands]);

    const totalSelectedDays = useMemo(() => {
        let n = 0;
        for (const set of clusterSelections.values()) n += set.size;
        return n;
    }, [clusterSelections]);

    // Total Current gross across all selected dates for the summary panel.
    const totalSelectedGross = useMemo(() => {
        if (!targetCurrent) return null;
        let total = 0;
        for (const island of roomIslands) {
            const selected = clusterSelections.get(island.id);
            if (!selected || selected.size === 0) continue;
            const memberIds = island.chain.map(c => c.reservationRoomId);
            const days = getClusterCurrentDays(memberIds, 'ROOM', targetCurrent);
            for (const d of days) {
                if (selected.has(d.date)) total += d.grossAmount;
            }
        }
        for (const island of programIslands) {
            const selected = clusterSelections.get(island.id);
            if (!selected || selected.size === 0) continue;
            const memberIds = island.chain.map(c => c.reservationProgramId);
            const days = getClusterCurrentDays(memberIds, 'PROGRAM', targetCurrent);
            for (const d of days) {
                if (selected.has(d.date)) total += d.grossAmount;
            }
        }
        return total;
    }, [targetCurrent, roomIslands, programIslands, clusterSelections]);

    const clusterCounts = useMemo(() => {
        let rooms = 0;
        let programs = 0;
        for (const island of roomIslands) {
            const selected = clusterSelections.get(island.id);
            if (selected && selected.size > 0) rooms++;
        }
        for (const island of programIslands) {
            const selected = clusterSelections.get(island.id);
            if (selected && selected.size > 0) programs++;
        }
        return { rooms, programs };
    }, [roomIslands, programIslands, clusterSelections]);

    const noClusters = roomIslands.length === 0 && programIslands.length === 0;

    return (
        <>
            <div className="modal-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                <div className="flex gap-3 mb-3">
                    <div className="flex-1">
                        <label className="form-label">Name *</label>
                        <input
                            type="text"
                            className="form-control"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder="e.g. High Season 2026 Rates"
                        />
                    </div>
                </div>
                <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                        className="form-control"
                        rows={2}
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                    />
                </div>

                {noClusters ? (
                    <div className="text-center py-4 text-gray-500">
                        This reservation has no rooms or programs to capture.
                    </div>
                ) : (
                    <div className="flex flex-col lg:flex-row gap-4">
                        <div className="flex-1">
                            <CreateClusterListPanel
                                roomIslands={roomIslands}
                                programIslands={programIslands}
                                clusterAllDates={clusterAllDates}
                                clusterSelections={clusterSelections}
                                editingClusterId={editingClusterId}
                                onEditCluster={setEditingClusterId}
                                targetCurrent={targetCurrent}
                            />
                        </div>

                        <div className="flex-1">
                            {editingClusterContext ? (
                                <CreateClusterEditorPanel
                                    cluster={editingClusterContext}
                                    allDates={clusterAllDates.get(editingClusterContext.id) ?? []}
                                    selectedDates={clusterSelections.get(editingClusterContext.id) ?? new Set()}
                                    onSelectedDatesChange={setSelectedDatesForEditingCluster}
                                    targetCurrent={targetCurrent}
                                    memberIds={editingClusterContext.memberIds}
                                    onClose={() => setEditingClusterId(null)}
                                    formatCurrency={formatCurrency}
                                />
                            ) : (
                                <CreateSummaryPanel
                                    totalSelectedDays={totalSelectedDays}
                                    totalSelectedGross={totalSelectedGross}
                                    targetCurrentLoaded={targetCurrent != null}
                                    clusterCounts={clusterCounts}
                                    formatCurrency={formatCurrency}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
            <div className="modal-footer">
                <button className="btn btn-outline-contrast" onClick={onClose}>Cancel</button>
                <button
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={!name.trim() || saving || totalSelectedDays === 0}
                >
                    {saving ? 'Saving...' : 'Save Snapshot'}
                </button>
            </div>
        </>
    );
}

interface SummaryProps {
    totalSelectedDays: number;
    totalSelectedGross: number | null;
    targetCurrentLoaded: boolean;
    clusterCounts: { rooms: number; programs: number };
    formatCurrency: (amount: number) => string;
}

function CreateSummaryPanel({
    totalSelectedDays, totalSelectedGross, targetCurrentLoaded, clusterCounts, formatCurrency,
}: SummaryProps) {
    if (totalSelectedDays === 0) {
        return (
            <div className="text-sm text-gray-500 py-3">
                No dates selected. Click <em>Edit</em> on a cluster to choose which dates to capture.
            </div>
        );
    }
    if (!targetCurrentLoaded) {
        return <div className="text-center py-4 text-gray-500">Loading current rates...</div>;
    }

    return (
        <div>
            <h6 className="font-semibold mb-2">Snapshot summary</h6>
            <div className="text-xs text-gray-600 mb-3">
                {totalSelectedDays} {totalSelectedDays === 1 ? 'date' : 'dates'} across{' '}
                {clusterCounts.rooms} {clusterCounts.rooms === 1 ? 'room' : 'rooms'} and{' '}
                {clusterCounts.programs} {clusterCounts.programs === 1 ? 'program' : 'programs'}.
            </div>
            {totalSelectedGross != null && (
                <div className="text-sm">
                    Current total gross: <span className="font-mono font-semibold">{formatCurrency(totalSelectedGross)}</span>
                </div>
            )}
        </div>
    );
}

// Standalone modal wrapper (used when not embedded in SnapshotsModal).
export function CreateRateSnapshotModal(props: Props) {
    return (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Create Rate Snapshot</h5>
                        <button type="button" className="btn-close" onClick={props.onClose}></button>
                    </div>
                    <CreateRateSnapshotPanel {...props} />
                </div>
            </div>
        </div>
    );
}
